<script setup>
import AbstractGroup from "./AbstractGroup.vue";
import Icon from "../../Common/Icon.vue";
import { computed } from "vue";
import Loader from "../../Common/Loader.vue";
import { watch } from "vue";
import { getLocale } from "../../../translations";
import { ref } from "vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object,
    required: true,
  },
});

const searchTerm = ref("");
const isLoading = ref(false);
const options = ref([]);

watch(searchTerm, async (newTerm, oldTerm) => {
  // Check if search term is valid
  if (!isSearchTermValid.value) {
    options.value = [];
    isLoading.value = false;
    return;
  }

  // Fetch options
  isLoading.value = true;
  const response = await fetch(
    `/api/klarnachrichten/${getLocale()}/search-filter-options/${props.name}?search=${encodeURIComponent(newTerm)}`
  );
  const json = await response.json();

  if (json.success) {
    options.value = json.data;
  }

  isLoading.value = false;
});

const activeOptionAmount = computed(() => {
  return Object.keys(props.modelValue).length;
});

const hasActiveOptions = computed(() => {
  return activeOptionAmount.value > 0;
});

const isSearchTermValid = computed(() => {
  return searchTerm.value.length >= 3;
});

function isOptionActive(id) {
  return props.modelValue[id]?.active;
}

function disableOption(id) {
  const tmp = props.modelValue;
  delete tmp[id];
  emit("update:modelValue", tmp);
}

function enableOption(option) {
  const tmp = props.modelValue;
  tmp[option.id] = {
    active: true,
    name: option.name,
    id: option.id,
  };
  emit("update:modelValue", tmp);
}

function toggleOption(option) {
  if (isOptionActive(option.id)) {
    disableOption(option.id);
    return;
  }
  enableOption(option);
}
</script>

<template>
  <AbstractGroup class="group">
    <template #header>
      <div :class="hasActiveOptions ? 'text-primary' : ''" class="transition group-hover:translate-x-2">
        <span v-t="`filter.group.${props.name}`"> </span>
        <span v-if="hasActiveOptions" v-text="` (${activeOptionAmount})`"> </span>
      </div>
    </template>
    <template #inner>
      <div class="text-16 lg:text-20">
        <div class="px-10 lg:px-16 py-6 flex items-center gap-x-4 border-b border-dark/10">
          <Icon name="search" class="shrink-0 size-4 text-dark/80" />
          <label :for="`${props.name}-search`" class="sr-only" v-t="`filter.group.${props.name}.search.label`"> </label>
          <input
            type="text"
            v-model.lazy="searchTerm"
            v-debounce="250"
            :id="`${props.name}-search`"
            :name="`${props.name}-search`"
            :placeholder="$t(`filter.group.${props.name}.search.placeholder`)"
            class="bg-transparent outline-none font-medium placeholder:text-dark/50 w-full grow"
          />
        </div>

        <!-- Active Options -->
        <div v-if="hasActiveOptions" class="px-10 lg:px-16 py-5 border-b border-dark/10 flex gap-4 flex-wrap">
          <button
            v-for="option in props.modelValue"
            :key="option.id"
            type="button"
            class="group/button shrink-0 bg-primary px-2 py-1 rounded-full text-white text-12 flex gap-x-2 items-center"
            @click="disableOption(option.id)"
          >
            <div class="shrink-0 relative size-2 overflow-hidden">
              <Icon
                name="check"
                class="absolute left-0 top-0 size-full transition group-hover/button:-translate-y-2 group-hover/button:opacity-0"
              />
              <Icon
                name="close"
                class="absolute left-0 top-0 size-full transition translate-y-2 opacity-0 group-hover/button:-translate-y-0 group-hover/button:opacity-100"
              />
            </div>

            <span v-text="option.name"></span>
          </button>
        </div>

        <div class="px-10 lg:px-16 py-6 lg:py-10">
          <!-- Options -->
          <div v-if="!isLoading && options.length > 0" class="space-y-4">
            <div v-for="option in options" :key="option.id" class="flex items-center gap-x-4">
              <input
                type="checkbox"
                :name="`${props.name}-option-${option.id}`"
                :id="`${props.name}-option-${option.id}`"
                class="accent-primary size-4"
                :value="option.id"
                @input="toggleOption(option)"
                :checked="isOptionActive(option.id)"
              />
              <label :for="`${props.name}-option-${option.id}`" v-text="option.name"> </label>
            </div>
          </div>

          <!-- Placeholder -->
          <p
            v-if="!isSearchTermValid && !isLoading && options.length === 0"
            v-t="`filter.group.${props.name}.options.placeholder`"
            class="text-12 lg:text-16 text-dark/50"
          ></p>

          <!-- No results -->
          <p
            v-if="isSearchTermValid && !isLoading && options.length === 0"
            v-t="`filter.group.${props.name}.options.no-results`"
            class="text-12 lg:text-16 text-dark/50"
          ></p>

          <!-- Loader -->
          <p v-if="isLoading" class="mx-auto">
            <Loader class="size-10 mx-auto text-dark" />
          </p>
        </div>
      </div>
    </template>
  </AbstractGroup>
</template>
